import React from 'react';
import { COLORS, DARK_THEME } from '../../constants/colors';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';

const PrivacyPolicy = () => {
  const { isDarkMode } = useTheme();
  const { language } = useLanguage();

  const pageBg = isDarkMode ? DARK_THEME.background : '#f3f8fa';
  const ink = isDarkMode ? DARK_THEME.textPrimary : '#0F2630';
  const muted = isDarkMode ? DARK_THEME.textSecondary : '#5b6b72';
  const line = isDarkMode ? DARK_THEME.border : '#e3eaed';
  const navy = isDarkMode ? DARK_THEME.primary : '#173D4D';
  const accent = isDarkMode ? DARK_THEME.primary : COLORS.primary;

  const s = {
    page: { backgroundColor: pageBg, color: ink, lineHeight: 1.65 },
    hero: {
      paddingTop: '120px',
      paddingBottom: '40px',
      paddingLeft: '20px',
      paddingRight: '20px',
      background: isDarkMode
        ? `linear-gradient(135deg, ${DARK_THEME.backgroundSecondary} 0%, ${DARK_THEME.background} 100%)`
        : `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryDark} 100%)`,
      textAlign: 'center',
    },
    heroTitle: { fontSize: 'clamp(28px, 5vw, 40px)', color: COLORS.white, fontWeight: 700, margin: '0 0 8px' },
    heroMeta: { color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem', margin: 0 },
    wrap: { maxWidth: '860px', margin: '0 auto', padding: '40px 20px 80px' },
    p: { fontSize: '1rem', margin: '0 0 14px' },
    h2: { fontSize: '1.3rem', margin: '36px 0 12px', color: navy, fontWeight: 700 },
    h3: { fontSize: '1.05rem', margin: '22px 0 8px', color: ink, fontWeight: 600 },
    ul: { paddingLeft: '22px', margin: '0 0 14px' },
    li: { fontSize: '1rem', marginBottom: '6px' },
    a: { color: accent, textDecoration: 'none' },
    fill: {
      backgroundColor: isDarkMode ? `${DARK_THEME.primary}1A` : '#eafafb',
      border: `1px solid ${isDarkMode ? DARK_THEME.border : '#bfe9ef'}`,
      borderRadius: '12px',
      padding: '18px 20px',
      margin: '22px 0',
      fontSize: '1rem',
      lineHeight: 1.8,
    },
    note: {
      backgroundColor: isDarkMode ? `${DARK_THEME.primary}14` : '#eafafb',
      border: `1px solid ${isDarkMode ? DARK_THEME.border : '#bfe9ef'}`,
      borderRadius: '12px',
      padding: '16px 18px',
      margin: '20px 0',
      fontSize: '1rem',
    },
    tableWrap: { overflowX: 'auto' },
    table: { width: '100%', borderCollapse: 'collapse', margin: '14px 0', fontSize: '0.95rem' },
    th: {
      textAlign: 'left',
      border: `1px solid ${line}`,
      padding: '10px 12px',
      backgroundColor: isDarkMode ? DARK_THEME.backgroundTertiary : '#eef4f6',
      color: ink,
      verticalAlign: 'top',
    },
    td: { textAlign: 'left', border: `1px solid ${line}`, padding: '10px 12px', color: muted, verticalAlign: 'top' },
    footer: {
      marginTop: '44px',
      paddingTop: '20px',
      borderTop: `1px solid ${line}`,
      color: muted,
      fontSize: '0.9rem',
    },
    em: { color: muted },
  };

  return (
    <div style={s.page}>
      <header style={s.hero}>
        <h1 style={s.heroTitle}>{language === 'tr' ? 'Gizlilik Politikası' : 'Privacy Policy'}</h1>
        <p style={s.heroMeta}>
          {language === 'tr' ? (
            <><strong>Turkic Silk Road</strong> mobil uygulaması · Son güncelleme: 17 Haziran 2026</>
          ) : (
            <><strong>Turkic Silk Road</strong> mobile application · Last updated: June 17, 2026</>
          )}
        </p>
      </header>

      <div style={s.wrap}>
        <p style={s.p}>
          {language === 'tr' ? (
            <>
              Bu Gizlilik Politikası, <strong>Turkic Silk Road</strong> mobil uygulamasını (“Uygulama”) kullanırken
              hangi kişisel verilerinizin işlendiğini, bu verilerin hangi amaçlarla ve hukuki sebeplerle işlendiğini,
              kimlerle paylaşıldığını ve sahip olduğunuz hakları açıklar. Uygulamayı kullanarak bu politikayı kabul
              etmiş olursunuz.
            </>
          ) : (
            <>
              This Privacy Policy explains which of your personal data is processed when you use the{' '}
              <strong>Turkic Silk Road</strong> mobile application (the “App”), for which purposes and on which
              legal bases it is processed, with whom it is shared, and the rights you have. By using the App,
              you accept this policy.
            </>
          )}
        </p>

        <div style={s.fill}>
          <strong>{language === 'tr' ? 'Veri Sorumlusu:' : 'Data Controller:'}</strong>{' '}
          {language === 'tr' ? 'Turkic Silk Road - Türk Devletleri Teşkilatı' : 'Turkic Silk Road - Organization of Turkic States'}<br />
          <strong>{language === 'tr' ? 'Adres:' : 'Address:'}</strong> Beyazıt, 34452 Fatih/İstanbul<br />
          <strong>{language === 'tr' ? 'İletişim / Başvuru:' : 'Contact / Requests:'}</strong>{' '}
          <a style={s.a} href="mailto:iletisim@turkicsilkroad.com">iletisim@turkicsilkroad.com</a>
        </div>

        <h2 style={s.h2}>{language === 'tr' ? '1. Topladığımız Veriler' : '1. Data We Collect'}</h2>

        <h3 style={s.h3}>{language === 'tr' ? 'a) Hesap ve profil bilgileri' : 'a) Account and profile information'}</h3>
        <ul style={s.ul}>
          <li style={s.li}>
            {language === 'tr' ? (
              <>E-posta adresi ve şifre (kimlik doğrulama Google Firebase Authentication ile yapılır; <strong>şifreniz tarafımızca görülmez veya açık şekilde saklanmaz</strong>).</>
            ) : (
              <>Email address and password (authentication is performed via Google Firebase Authentication; <strong>your password is never seen by us or stored in plain form</strong>).</>
            )}
          </li>
          <li style={s.li}>
            {language === 'tr'
              ? 'Görünen ad / ad-soyad; isteğe bağlı: telefon, doğum tarihi, cinsiyet, profil fotoğrafı (avatar).'
              : 'Display name / full name; optional: phone number, date of birth, gender, profile photo (avatar).'}
          </li>
          <li style={s.li}>
            {language === 'tr' ? 'Tercih edilen dil ve saat dilimi.' : 'Preferred language and time zone.'}
          </li>
          <li style={s.li}>
            {language === 'tr'
              ? 'İki adımlı doğrulamayı (2FA) etkinleştirirseniz buna ilişkin faktör bilgisi (Firebase tarafında).'
              : 'If you enable two-factor authentication (2FA), the related factor information (on the Firebase side).'}
          </li>
        </ul>

        <h3 style={s.h3}>{language === 'tr' ? 'b) Konum verisi' : 'b) Location data'}</h3>
        <p style={s.p}>
          {language === 'tr' ? (
            <>
              Konum yalnızca <strong>uygulama açıkken (when-in-use)</strong> ve siz ilgili bir özelliği kullandığınızda
              işlenir: yakındaki yerleri listeleme, gezi/rota planlama (TurkicRoute AI), “Buraya geldim” ziyaret kaydı ve
              haritadan konum seçme. <strong>Arka planda konum toplanmaz.</strong> İzni cihaz ayarlarından dilediğiniz zaman
              kapatabilirsiniz.
            </>
          ) : (
            <>
              Location is processed only <strong>while the App is open (when-in-use)</strong> and when you use a related
              feature: listing nearby places, trip/route planning (TurkicRoute AI), “I was here” visit logging, and
              selecting a location on the map. <strong>Location is not collected in the background.</strong> You can
              revoke the permission at any time from your device settings.
            </>
          )}
        </p>

        <h3 style={s.h3}>{language === 'tr' ? 'c) Kamera ve fotoğraf galerisi' : 'c) Camera and photo gallery'}</h3>
        <p style={s.p}>
          {language === 'tr' ? (
            <>
              Yalnızca siz bir işlem başlattığınızda (profil fotoğrafı yükleme veya bir yer bildirme/katkı görseli ekleme)
              kullanılır. Galeriniz veya kameranız sürekli izlenmez; yalnızca seçtiğiniz/çektiğiniz görseller işlenir.
            </>
          ) : (
            <>
              Used only when you initiate an action (uploading a profile photo or adding an image to a place report or
              contribution). Your gallery or camera is never monitored continuously; only the images you select or
              capture are processed.
            </>
          )}
        </p>

        <h3 style={s.h3}>{language === 'tr' ? 'd) Cihaz ve bildirim verileri' : 'd) Device and notification data'}</h3>
        <ul style={s.ul}>
          <li style={s.li}>
            {language === 'tr'
              ? 'Cihaza bağlı özellikler için anonim bir cihaz tanımlayıcısı.'
              : 'An anonymous device identifier for device-bound features.'}
          </li>
          <li style={s.li}>
            {language === 'tr'
              ? 'Bildirim izni verirseniz, anlık bildirim (push) gönderebilmek için bir bildirim jetonu (Google Firebase Cloud Messaging).'
              : 'If you grant notification permission, a notification token (Google Firebase Cloud Messaging) used to send push notifications.'}
          </li>
        </ul>

        <h3 style={s.h3}>{language === 'tr' ? 'e) Sizin oluşturduğunuz içerik' : 'e) Content you create'}</h3>
        <p style={s.p}>
          {language === 'tr' ? (
            <>
              Yorumlar ve puanlar, yer bildirimleri/düzenlemeleri, oluşturduğunuz veya kaydettiğiniz rotalar, gezi planları,
              ziyaretler, favoriler ve katkı/rozet verileri (isteğe bağlı eklediğiniz görsel ve konum dahil).
            </>
          ) : (
            <>
              Comments and ratings, place reports/edits, routes you create or save, trip plans, visits, favorites, and
              contribution/badge data (including any images and locations you optionally add).
            </>
          )}
        </p>

        <h3 style={s.h3}>{language === 'tr' ? 'f) Otomatik toplanan teknik veriler' : 'f) Automatically collected technical data'}</h3>
        <p style={s.p}>
          {language === 'tr'
            ? 'Hizmetin çalışması ve güvenliği için temel teknik kayıtlar (IP adresi, istek zamanı, hata/teşhis kayıtları).'
            : 'Basic technical records required for the operation and security of the service (IP address, request time, error/diagnostic logs).'}
        </p>

        <div style={s.note}>
          {language === 'tr' ? (
            <>
              <strong>Önemli:</strong> Uygulamada <strong>reklam ağı, analitik veya izleme (tracking) yazılımı kullanılmaz.</strong>{' '}
              Verilerinizi reklam amacıyla satmaz veya paylaşmayız.
            </>
          ) : (
            <>
              <strong>Important:</strong> The App uses <strong>no advertising networks, analytics, or tracking software.</strong>{' '}
              We do not sell or share your data for advertising purposes.
            </>
          )}
        </div>

        <h2 style={s.h2}>
          {language === 'tr'
            ? '2. Verileri Hangi Amaçlarla ve Hukuki Sebeple İşliyoruz'
            : '2. Purposes and Legal Bases of Processing'}
        </h2>
        <div style={s.tableWrap}>
          <table style={s.table}>
            <tbody>
              <tr>
                <th style={s.th}>{language === 'tr' ? 'Amaç' : 'Purpose'}</th>
                <th style={s.th}>{language === 'tr' ? 'Hukuki sebep (KVKK m.5 / GDPR m.6)' : 'Legal basis (Art. 5 KVKK / Art. 6 GDPR)'}</th>
              </tr>
              <tr>
                <td style={s.td}>{language === 'tr' ? 'Hesap oluşturma, giriş, e-posta doğrulama, 2FA' : 'Account creation, sign-in, email verification, 2FA'}</td>
                <td style={s.td}>{language === 'tr' ? 'Sözleşmenin ifası; açık rıza' : 'Performance of a contract; explicit consent'}</td>
              </tr>
              <tr>
                <td style={s.td}>{language === 'tr' ? 'Kaydettiğiniz içeriği (favoriler, geziler, ziyaretler, katkılar) saklama ve gösterme' : 'Storing and displaying the content you save (favorites, trips, visits, contributions)'}</td>
                <td style={s.td}>{language === 'tr' ? 'Sözleşmenin ifası' : 'Performance of a contract'}</td>
              </tr>
              <tr>
                <td style={s.td}>{language === 'tr' ? 'Konum, kamera/galeri, bildirim özellikleri' : 'Location, camera/gallery, and notification features'}</td>
                <td style={s.td}>{language === 'tr' ? 'Açık rıza (cihaz izinleri ile)' : 'Explicit consent (via device permissions)'}</td>
              </tr>
              <tr>
                <td style={s.td}>{language === 'tr' ? 'Güvenlik, kötüye kullanımın önlenmesi, hizmetin iyileştirilmesi' : 'Security, prevention of misuse, service improvement'}</td>
                <td style={s.td}>{language === 'tr' ? 'Meşru menfaat' : 'Legitimate interest'}</td>
              </tr>
              <tr>
                <td style={s.td}>{language === 'tr' ? 'Yasal yükümlülüklerin yerine getirilmesi' : 'Compliance with legal obligations'}</td>
                <td style={s.td}>{language === 'tr' ? 'Hukuki yükümlülük' : 'Legal obligation'}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h2 style={s.h2}>{language === 'tr' ? '3. Uygulama İzinleri' : '3. App Permissions'}</h2>
        <ul style={s.ul}>
          <li style={s.li}>
            {language === 'tr' ? (
              <><strong>Konum (yalnızca kullanımda):</strong> yakındaki yerler, rota/gezi planlama, ziyaret kaydı, haritadan seçim.</>
            ) : (
              <><strong>Location (while in use only):</strong> nearby places, route/trip planning, visit logging, selecting from the map.</>
            )}
          </li>
          <li style={s.li}>
            {language === 'tr' ? (
              <><strong>Kamera:</strong> yer bildirimi veya katkı görseli için fotoğraf çekme.</>
            ) : (
              <><strong>Camera:</strong> taking photos for place reports or contribution images.</>
            )}
          </li>
          <li style={s.li}>
            {language === 'tr' ? (
              <><strong>Fotoğraf galerisi:</strong> profil fotoğrafı veya katkı görseli seçme.</>
            ) : (
              <><strong>Photo gallery:</strong> selecting a profile photo or a contribution image.</>
            )}
          </li>
          <li style={s.li}>
            {language === 'tr' ? (
              <><strong>Bildirimler:</strong> etkinlik, haber ve güncellemeler için anlık bildirim.</>
            ) : (
              <><strong>Notifications:</strong> push notifications for events, news, and updates.</>
            )}
          </li>
        </ul>
        <p style={s.p}>
          {language === 'tr' ? (
            <>
              Tüm izinler isteğe bağlıdır ve cihaz ayarlarından istediğiniz zaman geri alınabilir. Uygulamanın çoğu özelliği
              (harita, rotalar, etkinlikler, haberler, üye devletler) giriş yapmadan ve bu izinler olmadan da kullanılabilir.
            </>
          ) : (
            <>
              All permissions are optional and can be revoked at any time from your device settings. Most features of the
              App (map, routes, events, news, member states) can be used without signing in and without these permissions.
            </>
          )}
        </p>

        <h2 style={s.h2}>{language === 'tr' ? '4. Verilerin Paylaşıldığı Üçüncü Taraflar' : '4. Third Parties with Whom Data Is Shared'}</h2>
        <p style={s.p}>
          {language === 'tr' ? (
            <>
              Kişisel verileriniz yalnızca hizmetin sağlanması için gerekli olan aşağıdaki hizmet sağlayıcılarla,
              yalnızca gerekli ölçüde paylaşılır:
            </>
          ) : (
            <>
              Your personal data is shared only with the following service providers required for the provision of the
              service, and only to the extent necessary:
            </>
          )}
        </p>
        <ul style={s.ul}>
          <li style={s.li}>
            {language === 'tr' ? (
              <><strong>Google (Firebase Authentication &amp; Cloud Messaging):</strong> kimlik doğrulama, e-posta doğrulama, iki adımlı doğrulama ve anlık bildirim jetonu.</>
            ) : (
              <><strong>Google (Firebase Authentication &amp; Cloud Messaging):</strong> authentication, email verification, two-factor authentication, and the push notification token.</>
            )}
          </li>
          <li style={s.li}>
            {language === 'tr' ? (
              <><strong>Sunucu altyapısı:</strong> Uygulamanın verileri, hizmeti yürüten sunucularda barındırılır.</>
            ) : (
              <><strong>Server infrastructure:</strong> The App’s data is hosted on the servers that operate the service.</>
            )}
          </li>
          <li style={s.li}>
            {language === 'tr' ? (
              <><strong>Harita ve rota hizmetleri:</strong> Açık kaynaklı harita karoları (OpenStreetMap tabanlı / CARTO) ve uydu görüntüleri (Esri) ile rota çizim hizmetleri. Haritayı kullandığınızda IP adresiniz ve görüntülenen alanın koordinatları ilgili sağlayıcılara teknik istek olarak iletilir.</>
            ) : (
              <><strong>Map and routing services:</strong> Open-source map tiles (OpenStreetMap-based / CARTO), satellite imagery (Esri), and route drawing services. When you use the map, your IP address and the coordinates of the displayed area are transmitted to the relevant providers as a technical request.</>
            )}
          </li>
        </ul>
        <p style={s.p}>
          {language === 'tr' ? (
            <>
              Bunların dışında verilerinizi üçüncü taraflara satmaz, kiralamaz veya pazarlama amacıyla paylaşmayız. Yasal
              olarak zorunlu hâllerde yetkili kamu kurumlarıyla paylaşım yapılabilir.
            </>
          ) : (
            <>
              Beyond these, we do not sell, rent, or share your data with third parties for marketing purposes. Where
              legally required, data may be shared with competent public authorities.
            </>
          )}
        </p>

        <h2 style={s.h2}>{language === 'tr' ? '5. Çerezler ve İzleme' : '5. Cookies and Tracking'}</h2>
        <p style={s.p}>
          {language === 'tr' ? (
            <>
              Mobil uygulama reklam çerezi veya üçüncü taraf izleme teknolojisi kullanmaz. Oturumunuzu sürdürmek için
              gerekli kimlik jetonları cihazınızda güvenli alanda (Secure Store) saklanır.
            </>
          ) : (
            <>
              The mobile application does not use advertising cookies or third-party tracking technologies. The identity
              tokens required to maintain your session are stored in a secure area on your device (Secure Store).
            </>
          )}
        </p>

        <h2 style={s.h2}>{language === 'tr' ? '6. Verilerin Saklanması' : '6. Data Retention'}</h2>
        <p style={s.p}>
          {language === 'tr' ? (
            <>
              Kişisel verileriniz, hesabınız aktif olduğu ve ilgili amaç için gerekli olduğu sürece saklanır. Yasal saklama
              yükümlülükleri saklıdır. Hesabınızı sildiğinizde aşağıdaki bölüm uygulanır.
            </>
          ) : (
            <>
              Your personal data is retained for as long as your account is active and as necessary for the relevant
              purpose. Statutory retention obligations remain reserved. When you delete your account, the following
              section applies.
            </>
          )}
        </p>

        <h2 style={s.h2}>{language === 'tr' ? '7. Hesabınızı ve Verilerinizi Silme' : '7. Deleting Your Account and Data'}</h2>
        <p style={s.p}>
          {language === 'tr' ? (
            <>
              Hesabınızı uygulama içinden <em style={s.em}>Hesap → Hesabı kalıcı sil</em> adımıyla veya{' '}
              <a style={s.a} href="mailto:iletisim@turkicsilkroad.com">iletisim@turkicsilkroad.com</a> üzerinden silinmesini talep ederek
              kaldırabilirsiniz. Silme işleminde:
            </>
          ) : (
            <>
              You can remove your account from within the App via <em style={s.em}>Account → Permanently delete account</em>,
              or by requesting deletion via{' '}
              <a style={s.a} href="mailto:iletisim@turkicsilkroad.com">iletisim@turkicsilkroad.com</a>. Upon deletion:
            </>
          )}
        </p>
        <ul style={s.ul}>
          <li style={s.li}>
            {language === 'tr'
              ? 'Kişisel verileriniz (ad, e-posta, telefon, doğum tarihi vb.) silinir veya anonimleştirilir.'
              : 'Your personal data (name, email, phone number, date of birth, etc.) is deleted or anonymized.'}
          </li>
          <li style={s.li}>
            {language === 'tr' ? (
              <>Daha önce herkese açık yayınladığınız katkılar (ör. yorum/rota) topluluğun bütünlüğü için “Turkic Silk Road Kullanıcısı” olarak <strong>anonim</strong> biçimde korunabilir.</>
            ) : (
              <>Contributions you previously published publicly (e.g., comments/routes) may be retained in <strong>anonymous</strong> form as “Turkic Silk Road User” to preserve the integrity of the community.</>
            )}
          </li>
          <li style={s.li}>
            {language === 'tr'
              ? 'Cihaza bağlı verileri uygulama içinden ayrıca temizleyebilirsiniz.'
              : 'You can additionally clear device-bound data from within the App.'}
          </li>
        </ul>

        <h2 style={s.h2}>{language === 'tr' ? '8. Veri Güvenliği' : '8. Data Security'}</h2>
        <p style={s.p}>
          {language === 'tr' ? (
            <>
              Veriler aktarım sırasında HTTPS/TLS ile şifrelenir; oturum jetonları cihazda güvenli depolama alanında tutulur;
              şifreler kimlik sağlayıcı (Firebase) tarafında özetlenmiş (hash) olarak saklanır. Hiçbir yöntem %100 güvenli
              olmasa da verilerinizi korumak için makul teknik ve idari tedbirleri uygularız.
            </>
          ) : (
            <>
              Data is encrypted in transit using HTTPS/TLS; session tokens are kept in secure storage on the device; and
              passwords are stored in hashed form by the identity provider (Firebase). Although no method is 100% secure,
              we apply reasonable technical and administrative measures to protect your data.
            </>
          )}
        </p>

        <h2 style={s.h2}>{language === 'tr' ? '9. Uluslararası Veri Aktarımı' : '9. International Data Transfers'}</h2>
        <p style={s.p}>
          {language === 'tr' ? (
            <>
              Kimlik doğrulama ve bildirim hizmetleri (Google/Firebase) ile bazı altyapı bileşenleri, sunucuları yurt dışında
              bulunan sağlayıcılar tarafından işletilebilir. Bu aktarımlar yürürlükteki mevzuata (KVKK / GDPR) uygun şekilde
              gerçekleştirilir.
            </>
          ) : (
            <>
              Authentication and notification services (Google/Firebase) and certain infrastructure components may be
              operated by providers whose servers are located abroad. These transfers are carried out in accordance with
              the applicable legislation (KVKK / GDPR).
            </>
          )}
        </p>

        <h2 style={s.h2}>{language === 'tr' ? '10. Çocukların Gizliliği' : '10. Children’s Privacy'}</h2>
        <p style={s.p}>
          {language === 'tr' ? (
            <>
              Uygulama 13 yaşın (bazı ülkelerde 16) altındaki çocuklara yönelik değildir ve bilerek çocuklardan kişisel veri
              toplamayız. Bir çocuğa ait veri işlediğimizi fark edersek sileriz.
            </>
          ) : (
            <>
              The App is not directed at children under the age of 13 (16 in some countries), and we do not knowingly
              collect personal data from children. If we become aware that we have processed data belonging to a child,
              we delete it.
            </>
          )}
        </p>

        <h2 style={s.h2}>{language === 'tr' ? '11. Haklarınız' : '11. Your Rights'}</h2>
        <p style={s.p}>
          {language === 'tr' ? (
            <>
              KVKK (6698 sayılı Kanun) m.11 ve GDPR kapsamında; kişisel verilerinizin işlenip işlenmediğini öğrenme,
              bilgi talep etme, düzeltme, silme/yok edilmesini isteme, işlemenin sınırlandırılmasını veya işlemeye itiraz etme,
              veri taşınabilirliği ve verdiğiniz rızayı geri çekme haklarına sahipsiniz. Taleplerinizi{' '}
              <a style={s.a} href="mailto:iletisim@turkicsilkroad.com">iletisim@turkicsilkroad.com</a> adresine iletebilirsiniz; başvurunuz
              yasal süreler içinde yanıtlanır.
            </>
          ) : (
            <>
              Under Article 11 of the KVKK (Law No. 6698) and the GDPR, you have the right to learn whether your personal
              data is processed, to request information, rectification, or erasure/destruction, to request the restriction
              of processing or object to processing, to data portability, and to withdraw any consent you have given.
              You may submit your requests to{' '}
              <a style={s.a} href="mailto:iletisim@turkicsilkroad.com">iletisim@turkicsilkroad.com</a>; your request will be
              answered within the statutory time limits.
            </>
          )}
        </p>

        <h2 style={s.h2}>{language === 'tr' ? '12. Politikadaki Değişiklikler' : '12. Changes to This Policy'}</h2>
        <p style={s.p}>
          {language === 'tr' ? (
            <>
              Bu politikayı zaman zaman güncelleyebiliriz. Önemli değişiklikleri uygulama içinden veya bu sayfa üzerinden
              duyururuz; sayfa başındaki “Son güncelleme” tarihi geçerli sürümü gösterir.
            </>
          ) : (
            <>
              We may update this policy from time to time. We will announce significant changes within the App or on this
              page; the “Last updated” date at the top of the page indicates the current version.
            </>
          )}
        </p>

        <h2 style={s.h2}>{language === 'tr' ? '13. İletişim' : '13. Contact'}</h2>
        <p style={s.p}>
          {language === 'tr' ? 'Gizlilikle ilgili tüm soru ve talepleriniz için:' : 'For all privacy-related questions and requests:'}{' '}
          <a style={s.a} href="mailto:iletisim@turkicsilkroad.com">iletisim@turkicsilkroad.com</a>
        </p>

        <footer style={s.footer}>
          {language === 'tr'
            ? '© 2026 Turkic Silk Road. Bu belge bilgilendirme amaçlıdır.'
            : '© 2026 Turkic Silk Road. This document is provided for informational purposes.'}
        </footer>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
